<template>
  <div class="revenue-management-page">
    <!-- Header Section -->
    <header class="dashboard-header">
      <div class="header-content">
        <h1>Revenue Management</h1>
        <p class="subtitle">Add and manage revenue sources</p>
      </div>
    </header>

    <!-- Main Content -->
    <div class="management-container">
      <!-- Month Selection -->
      <div class="month-selector">
        <div class="select-wrapper">
          <label for="year-select">Year</label>
          <select 
            id="year-select"
            v-model="selectedYear" 
            class="select-input" 
            @change="handleYearChange"
          >
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
          </select>
        </div>
        <div class="select-wrapper">
          <label for="month-select">Month</label>
          <select 
            id="month-select"
            v-model="selectedMonth" 
            class="select-input" 
            @change="handleMonthChange"
          >
            <option 
              v-for="month in availableMonths" 
              :key="month.value" 
              :value="month.value"
              :disabled="month.disabled"
            >
              {{ month.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Revenue Overview -->
      <div class="revenue-overview card">
        <h2>Revenue Overview</h2>
        <div class="total-revenue">
          <span class="label">Total Revenue:</span>
          <span class="amount"><CurrencyDisplay :value="totalRevenue" size="large" /></span>
        </div>
        <div class="in-store-revenue">
          <span class="label">In-Store Revenue:</span>
          <span class="amount"><CurrencyDisplay :value="inStoreRevenue" size="large" /></span>
        </div>
        <div class="other-sources-revenue">
          <span class="label">Other Sources:</span>
          <span class="amount"><CurrencyDisplay :value="otherSourcesRevenue" size="large" /></span>
        </div>
      </div>

      <!-- Revenue Sources -->
      <div class="revenue-sources card">
        <div class="card-header">
          <h2>Revenue Sources</h2>
          <button 
            class="btn-primary"
            @click="showAddSourceModal = true"
          >
            Add Revenue Source
          </button>
        </div>

        <div class="table-container">
          <table>
            <thead>
              <tr>
                <th>Source Name</th>
                <th>Amount</th>
                <th>Percentage</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="source in revenueSources" :key="source.id">
                <td>{{ source.name }}</td>
                <td><CurrencyDisplay :value="source.amount" /></td>
                <td>{{ calculatePercentage(source.amount) }}%</td>
                <td class="actions">
                  <button 
                    class="action-btn edit"
                    @click="editSource(source)"
                    title="Edit Source"
                  >
                    <i class="fa-solid fa-edit"></i>
                  </button>
                  <button 
                    class="action-btn delete"
                    @click="confirmDelete(source)"
                    title="Delete Source"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add/Edit Source Modal -->
    <div class="modal" v-if="showAddSourceModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ editingSource ? 'Edit' : 'Add' }} Revenue Source</h2>
          <button class="close-btn" @click="closeSourceModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSourceSubmit">
            <div class="form-group">
              <label for="sourceName">Source Name</label>
              <input 
                type="text" 
                id="sourceName" 
                v-model="sourceForm.name"
                required
                placeholder="e.g., Sales, Incentives, Bonus"
              />
            </div>
            <div class="form-group">
              <label for="sourceAmount">Amount (RM)</label>
              <input 
                type="number" 
                id="sourceAmount" 
                v-model="sourceForm.amount"
                required
                min="0.01"
                step="0.01"
                @input="validateAmount($event.target.value)"
              />
              <small v-if="!validateAmount(sourceForm.amount)" class="error-text">
                Amount must be greater than 0
              </small>
            </div>
            <div class="form-group" v-if="reportingBasis === 'daily'">
              <label for="sourceDate">Date</label>
              <input 
                type="date" 
                id="sourceDate" 
                v-model="sourceForm.date"
                required
                :max="maxDate"
                :min="minDate"
              />
            </div>
            <div class="form-group" v-if="reportingBasis === 'weekly'">
              <label for="sourceWeek">Week</label>
              <select 
                id="sourceWeek" 
                v-model="sourceForm.week"
                required
                class="select-input"
              >
                <option value="">Select Week</option>
                <option 
                  v-for="week in availableWeeks" 
                  :key="week.value" 
                  :value="week.value"
                >
                  {{ week.label }}
                </option>
              </select>
            </div>
            <div class="form-actions">
              <button type="button" class="btn-secondary" @click="closeSourceModal">Cancel</button>
              <button 
                type="submit" 
                class="btn-primary" 
                :disabled="isLoading || !validateAmount(sourceForm.amount)"
              >
                {{ isLoading ? 'Saving...' : 'Save Source' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal" v-if="showDeleteModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Confirm Delete</h2>
          <button class="close-btn" @click="closeDeleteModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete this revenue source? This action cannot be undone.</p>
          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="closeDeleteModal">Cancel</button>
            <button type="button" class="btn-danger" @click="handleDelete" :disabled="isLoading">
              {{ isLoading ? 'Deleting...' : 'Delete Source' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div class="toast-container" v-if="showToast">
      <div :class="['toast', toastType]">
        <span class="icon" v-if="toastType === 'success'">✓</span>
        {{ toastMessage }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router = useRouter()
const isLoading = ref(false)
const showToast = ref(false)
const toastMessage = ref('')
const toastType = ref('')
const selectedYear = ref('')
const selectedMonth = ref('')
const revenueSources = ref([])
const showAddSourceModal = ref(false)
const showDeleteModal = ref(false)
const editingSource = ref(null)
const totalRevenue = ref(0)
const inStoreRevenue = ref(0)
const otherSourcesRevenue = ref(0)
const reportingBasis = ref('daily')

const sourceForm = ref({
  name: '',
  amount: '',
  week: '',
  date: ''
})

// API instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const showNotification = (message, type = 'success') => {
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  setTimeout(() => {
    showToast.value = false
  }, 3000)
}

const formatNumber = (number) => {
  if (isNaN(number) || number === null || number === undefined) {
    return '0.00'
  }
  return new Intl.NumberFormat('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(number)
}

const calculatePercentage = (amount) => {
  const total = totalRevenue.value
  if (total === 0) return 0
  return ((amount / total) * 100).toFixed(2)
}

const fetchRevenueData = async () => {
  try {
    isLoading.value = true;
    const userData = JSON.parse(sessionStorage.getItem('user'));
    const subsidiaryId = userData.subsidiary_id;
    const year = parseInt(selectedYear.value);
    const month = parseInt(selectedMonth.value);
    
    
    // Validate date is not in future
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    if (year > currentYear || (year === currentYear && month > currentMonth)) {
      showNotification('Cannot view revenue data for future dates', 'error');
      revenueSources.value = [];
      totalRevenue.value = 0;
      inStoreRevenue.value = 0;
      otherSourcesRevenue.value = 0;
      return;
    }

    // Reset data first
    revenueSources.value = [];
    totalRevenue.value = 0;
    inStoreRevenue.value = 0;
    otherSourcesRevenue.value = 0;

    // Construct date range for the selected month
    const startDate = `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}-01`;
    const endDate = `${selectedYear.value}-${String(selectedMonth.value).padStart(2, '0')}-31`;



    // Get the subsidiary's reporting basis
    const subsidiaryResponse = await api.get(`/api/subsidiary/${subsidiaryId}`);
    reportingBasis.value = subsidiaryResponse.data.reporting_basis;

    // Fetch daily/weekly report data
    let reportData;
    if (reportingBasis.value === 'daily') {
      reportData = await api.get(`/api/revenue/daily`, {
        params: {
          subsidiary_id: subsidiaryId,
          start_date: startDate,
          end_date: endDate
        }
      });
    } else {
      // For weekly reports, we need to handle the weeks that overlap with the month
      const startDate = new Date(selectedYear.value, selectedMonth.value - 1, 1);
      const endDate = new Date(selectedYear.value, selectedMonth.value, 0);
      
      // Adjust start date to the beginning of the week (Sunday)
      const startDay = startDate.getDay();
      startDate.setDate(startDate.getDate() - startDay);
      
      // Adjust end date to the end of the week (Saturday)
      const endDay = endDate.getDay();
      endDate.setDate(endDate.getDate() + (6 - endDay));
      


      reportData = await api.get(`/api/revenue/weekly`, {
        params: {
          subsidiary_id: subsidiaryId,
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
    }



    // Calculate in-store revenue from reports
    if (reportingBasis.value === 'daily') {
      inStoreRevenue.value = parseFloat(reportData.data.reports_revenue) || 0;
    } else {
      // For weekly reports, use the reports_revenue directly
      inStoreRevenue.value = parseFloat(reportData.data.reports_revenue) || 0;
    }

    // Get revenue sources directly from database
    const sourcesResponse = await api.get(`/api/revenue/sources`, {
      params: {
        subsidiary_id: subsidiaryId,
        year: year,
        month: month
      }
    });
    

    
    // Set revenue sources directly from response
    revenueSources.value = sourcesResponse.data || [];
    
    // Calculate other sources revenue
    otherSourcesRevenue.value = revenueSources.value.reduce((sum, source) => {
      const amount = parseFloat(source.amount) || 0;
      return sum + amount;
    }, 0);
    
    // Calculate total revenue (sum of both)
    totalRevenue.value = inStoreRevenue.value + otherSourcesRevenue.value;
    

  } catch (error) {
    showNotification('Error fetching revenue data', 'error');
    // Reset data on error
    revenueSources.value = [];
    totalRevenue.value = 0;
    inStoreRevenue.value = 0;
    otherSourcesRevenue.value = 0;
  } finally {
    isLoading.value = false;
  }
};

const editSource = (source) => {
  editingSource.value = source
  sourceForm.value = {
    name: source.name,
    amount: source.amount,
    week: source.week,
    date: source.date || ''
  }
  showAddSourceModal.value = true
}

const closeSourceModal = () => {
  showAddSourceModal.value = false
  editingSource.value = null
  sourceForm.value = {
    name: '',
    amount: '',
    week: '',
    date: ''
  }
}

const validateAmount = (amount) => {
  const currentAmount = parseFloat(amount) || 0
  return currentAmount > 0
}

const handleSourceSubmit = async () => {
  try {
    isLoading.value = true;
    const userData = JSON.parse(sessionStorage.getItem('user'));
    const subsidiaryId = userData.subsidiary_id;
    const year = parseInt(selectedYear.value);
    const month = parseInt(selectedMonth.value);
    
    // Get the subsidiary's reporting basis first
    const subsidiaryResponse = await api.get(`/api/subsidiary/${subsidiaryId}`);
    const reportingBasis = subsidiaryResponse.data.reporting_basis;
    
    // Validate amount
    if (!validateAmount(sourceForm.value.amount)) {
      showNotification('Amount must be greater than 0', 'error');
      return;
    }

    // Validate date only for daily reporting basis
    if (reportingBasis === 'daily' && !sourceForm.value.date) {
      showNotification('Date is required', 'error');
      return;
    }

    // Validate week for weekly reporting basis
    if (reportingBasis === 'weekly' && !sourceForm.value.week) {
      showNotification('Week is required', 'error');
      return;
    }

    // Format the data according to the revenue_source table structure
    const data = {
      name: sourceForm.value.name,
      amount: parseFloat(sourceForm.value.amount),
      subsidiary_id: subsidiaryId,
      year: year.toString(),
      month: month.toString(),
      description: null,
      week: reportingBasis === 'weekly' ? sourceForm.value.week : null,
      date: reportingBasis === 'daily' ? sourceForm.value.date : null
    };

      

    if (editingSource.value) {
      const updateData = {
        ...data,
        id: editingSource.value.id
      };
      await api.put(`/api/revenue/source/${editingSource.value.id}`, updateData);
      showNotification('Revenue source updated successfully');
    } else {
      await api.post('/api/revenue/source', data);
      showNotification('Revenue source added successfully');
    }

    await fetchRevenueData();
    closeSourceModal();
  } catch (error) {
    if (error.response?.data?.error?.includes('unique_source_name')) {
      showNotification('A revenue source with this name already exists for this month', 'error');
    } else {
      showNotification(error.response?.data?.error || 'Failed to save revenue source', 'error');
    }
  } finally {
    isLoading.value = false;
  }
};

const confirmDelete = (source) => {
  editingSource.value = source
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  editingSource.value = null
}

const handleDelete = async () => {
  try {
    isLoading.value = true;
    if (!editingSource.value || !editingSource.value.id) {
      showNotification('Invalid source to delete', 'error');
      return;
    }
    
    await api.delete(`/api/revenue/source/${editingSource.value.id}`);
    showNotification('Revenue source deleted successfully');
    await fetchRevenueData();
    closeDeleteModal();
  } catch (error) {
    showNotification('Failed to delete revenue source', 'error');
  } finally {
    isLoading.value = false;
  }
};

// Get current year and month
const currentDate = new Date();
const currentYear = currentDate.getFullYear();
const currentMonth = currentDate.getMonth() + 1;

// Available years (current year and 2 years before)
const availableYears = computed(() => {
  const currentYear = new Date().getFullYear();
  return [currentYear - 2, currentYear - 1, currentYear];
});

// Month names
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// Add computed property for available months
const availableMonths = computed(() => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  return months.map((month, index) => {
    const monthNumber = index + 1;
    const isAvailable = selectedYear.value < currentYear || 
                       (selectedYear.value === currentYear && monthNumber <= currentMonth);
    return {
      name: month,
      value: monthNumber,
      disabled: !isAvailable
    };
  });
});

// Add computed property for available weeks
const availableWeeks = computed(() => {
  if (!selectedYear.value || !selectedMonth.value) return [];
  
  const year = parseInt(selectedYear.value);
  const month = parseInt(selectedMonth.value);
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  
  const weeks = [];
  let currentDate = new Date(firstDay);
  
  while (currentDate <= lastDay) {
    const weekNumber = Math.ceil(currentDate.getDate() / 7);
    const startDate = currentDate.getDate();
    let endDate;
    
    if (weekNumber === 5) {
      endDate = lastDay.getDate();
    } else {
      endDate = Math.min(startDate + 6, lastDay.getDate());
    }
    
    weeks.push({
      value: weekNumber,
      label: `Week ${weekNumber} (${startDate}-${endDate})`
    });
    
    currentDate.setDate(currentDate.getDate() + 7);
  }
  
  return weeks;
});

// Add computed properties for date constraints
const maxDate = computed(() => {
  const lastDay = new Date(selectedYear.value, selectedMonth.value, 0);
  return lastDay.toISOString().split('T')[0];
});

const minDate = computed(() => {
  const firstDay = new Date(selectedYear.value, selectedMonth.value - 1, 1);
  return firstDay.toISOString().split('T')[0];
});

// Add watch for year and month changes
watch([selectedYear, selectedMonth], ([newYear, newMonth]) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  // If future date is selected, reset to current date
  if (newYear > currentYear || (newYear === currentYear && newMonth > currentMonth)) {
    selectedYear.value = currentYear;
    selectedMonth.value = currentMonth;
    showNotification('Cannot view revenue data for future dates', 'error');
  }
}, { immediate: true });

// Add year change handler
const handleYearChange = () => {
  // If selected month is in the future for the new year, reset to current month
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  if (selectedYear.value === currentYear && selectedMonth.value > currentMonth) {
    selectedMonth.value = currentMonth;
  }
  fetchRevenueData();
};

// Update month change handler
const handleMonthChange = () => {
  const currentDate = new Date();
  const selectedDate = new Date(selectedYear.value, selectedMonth.value - 1);
  
  if (selectedDate > currentDate) {
    showNotification('Cannot view revenue data for future dates', 'error');
    // Reset to current month
    selectedMonth.value = currentDate.getMonth() + 1;
    return;
  }
  fetchRevenueData();
};

onMounted(() => {
  // Set default month to current month
  const currentDate = new Date();
  selectedYear.value = currentDate.getFullYear();
  selectedMonth.value = currentDate.getMonth() + 1;
  
  // Check if user is admin
  const userData = sessionStorage.getItem('user')
  if (userData) {
    const user = JSON.parse(userData)
    if (user.role !== 'Admin' && user.role !== 'admin') {
      router.push('/')
    } else {
      fetchRevenueData()
    }
  } else {
    router.push('/')
  }
})
</script>

<style scoped>
.revenue-management-page {
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.header-content h1 {
  font-size: 1.875rem;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.025em;
}

.subtitle {
  font-size: 0.875rem;
  color: #64748b;
}

.management-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.month-selector {
  display: flex;
  gap: 1.5rem;
  align-items: flex-end;
  margin-bottom: 1rem;
}

.select-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 160px;
}

.select-wrapper label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
}

.select-input {
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1e293b;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  background-size: 1.25rem;
  padding-right: 2.5rem;
}

.select-input:hover {
  border-color: #cbd5e1;
}

.select-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.select-input option {
  padding: 0.5rem;
  font-size: 0.875rem;
}

.card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(226, 232, 240, 0.8);
  padding: 24px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.card-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.revenue-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
}

.total-revenue,
.in-store-revenue,
.other-sources-revenue {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  font-size: 0.875rem;
  color: #64748b;
}

.amount {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
}

.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

th {
  background: #f8fafc;
  font-weight: 600;
  color: #64748b;
  font-size: 0.875rem;
}

td {
  color: #1e293b;
  font-size: 0.875rem;
}

.actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  padding: 6px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn.edit {
  color: #2563eb;
}

.action-btn.delete {
  color: #dc2626;
}

.action-btn:hover {
  opacity: 0.8;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #f8fafc;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.close-btn {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.helper-text {
  display: block;
  margin-top: 4px;
  font-size: 0.75rem;
  color: #64748b;
}

.error-text {
  display: block;
  margin-top: 4px;
  font-size: 0.75rem;
  color: #dc2626;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn-danger {
  background: #dc2626;
  color: white;
}

.btn-danger:hover {
  background: #b91c1c;
}

.btn-primary:disabled,
.btn-secondary:disabled,
.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.toast-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 1000;
}

.toast {
  padding: 16px;
  border-radius: 12px;
  font-weight: 500;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: slideIn 0.3s ease-out;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
}

.toast.success {
  background: #dcfce7;
  color: #16a34a;
}

.toast.error {
  background: #fee2e2;
  color: #dc2626;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .revenue-management-page {
    padding: 16px;
  }

  .revenue-overview {
    grid-template-columns: 1fr;
  }

  .table-container {
    margin: 0 -16px;
  }

  table {
    min-width: 800px;
  }

  .modal-content {
    margin: 16px;
    max-height: calc(100vh - 32px);
  }

  .month-selector {
    flex-direction: column;
    gap: 1rem;
  }

  .select-wrapper {
    width: 100%;
  }
}
</style> 